import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import Forms from '../../../components/Forms';
import Error from '../../../components/Error';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';

const DiscographyAlbumModal = ({ close, onAlbumUpdate, album }) => {
    const {t} = useTranslation();
    const inputRef = useRef(null);
    const isEdit = Boolean(album?._id);

    const [type, setType] = useState(album?.type || '');
    const [year, setYear] = useState(album?.year || '');
    const [name, setName] = useState(album?.name || '');
    const [description, setDescription] = useState(album?.description || '');
    const [songList, setSongList] = useState(album?.songList || '');
    const [purchaseUrl, setPurchaseUrl] = useState(album?.purchaseUrl || '');
    const [imageFile, setImageFile] = useState(null);
    const [imagePath, setImagePath] = useState(album?.previewImg || null);
    const [deleteImage, setDeleteImage] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => { setTimeout(() => inputRef.current?.focus(), 1); }, []);

    useEffect(() => {
        // při otevření s jiným záznamem
        setType(album?.type || '');
        setName(album?.name || '');
        setDescription(album?.description || '');
        setSongList(album?.songList || '');
        setPurchaseUrl(album?.purchaseUrl || '');
        setYear(album?.year || '');
        setImageFile(null);
        setImagePath(album?.previewImg || null);
        setDeleteImage(false);
        setError('');
    }, [album]);

    const errors = useMemo(() => {
        const e = {};
        if (!type) e.type = t('validation.required');
        if (!name.trim()) e.name = t('validation.required');
        if (!isEdit && !imageFile) e.previewImg = t('validation.required'); // u nového je obal povinný
        return e;
    }, [type, name, imageFile, isEdit, t]);

    const typeOptions = [
        { value: 'lp', label: t('discography.lp') },
        { value: 'mc', label: t('discography.mc') },
        { value: 'cd', label: t('discography.cd') },
        { value: 'sp', label: t('discography.sp') }
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        // if (Object.keys(errors).length) return;

        const formData = new FormData();
        formData.append('type', type);
        formData.append('name', name.trim());
        formData.append('year', year);
        formData.append('description', description.trim());
        formData.append('purchaseUrl', purchaseUrl.trim());
        formData.append('songList', songList.trim());

        // nový soubor?
        if (imageFile) {
            formData.append('previewImg', imageFile);
        }

        // mazání stávajícího?
        if (isEdit) {
            formData.append('deleteImage', deleteImage ? 'true' : 'false');
        }

        try {
            if (isEdit) {
                await API.put(API_ENDPOINTS.discographyAlbums.update(album._id), formData);
            } else {
                await API.post(API_ENDPOINTS.discographyAlbums.create, formData);
            }
            onAlbumUpdate?.();
            close();
        } catch (err) {
            setError(err);
            console.error('Failed to upsert discography album:', err);
        }
    };

    const renderPreview = () => {
        const src = imageFile
            ? URL.createObjectURL(imageFile)
            : imagePath;

        if (!src) return null;
        return (
            <div className="mt-2">
                <img src={src} alt="preview" className="h-40 w-40 object-cover rounded border" />
            </div>
        );
    };

    return (
        <form onSubmit={handleSubmit}>
            <Typography variant="h6" component="h2" className="mb-2">
                {isEdit ? t('discography.editAlbum') : t('discography.addAlbum')}
            </Typography>

            <div className="grid grid-cols-2 gap-4 mb-4 items-start">
                <Forms.Select
                    required
                    label={t('discography.label')}
                    value={type ? { value: type, label: t(`discography.${type}`) } : null}
                    options={typeOptions}
                    onChange={(opt) => setType(opt?.value || '')}
                    error={errors.type}
                    className="rounded-md"
                />

                <Forms.Text
                    ref={inputRef}
                    required
                    label={t('discography.albumName')}
                    placeholder={t('discography.albumName')}
                    value={name}
                    onChange={setName}
                    error={errors.name}
                    className="rounded-md"
                />

                <Forms.Text
                    label={t('discography.year')}
                    placeholder={t('discography.year')}
                    value={year}
                    onChange={setYear}
                    error={errors.name}
                    className="rounded-md"
                />

                <Forms.Text
                    label={t('discography.purchaseUrl')}
                    placeholder={t('discography.purchaseUrl')}
                    value={purchaseUrl}
                    onChange={setPurchaseUrl}
                    className="rounded-md"
                />

                <Forms.Textarea
                    label={t('discography.description')}
                    placeholder={t('discography.description')}
                    value={description}
                    onChange={setDescription}
                    className="rounded-md"
                    colspan={2}
                />

                <Forms.Textarea
                    label={t('discography.songList')}
                    placeholder={t('discography.songList')}
                    value={songList}
                    onChange={setSongList}
                    className="rounded-md"
                    colspan={2}
                />

                <div className="col-span-2">
                    <Forms.File
                        label={t('discography.cover')}
                        onChange={file => { setImageFile(file); setDeleteImage(false); }}
                        error={errors.previewImg}
                    />
                    {imagePath && isEdit && (
                        <Forms.Checkbox
                            name="deleteImage"
                            label={t('discography.deleteCover')}
                            value={deleteImage}
                            onChange={setDeleteImage}
                            className="mt-2"
                        />
                    )}
                    {renderPreview()}
                </div>
            </div>

            {error && <Error error={error} />}

            <Forms.Button submit label={t('save')} className="mt-4" />
        </form>
    );
};

export default DiscographyAlbumModal;
